import cn from 'classnames/bind'
import { ReactNode, useCallback, useState, memo } from 'react'
import { isEmpty } from 'ramda'

import Sticky from 'app/common/templates-next/sticky'
import {
  useCopyContentWidths,
  useHoverByClass,
  useSyncRowsHeightsV2,
  useCopyCellsOfTr,
} from 'app/common/ui-components/table'

import HScrollbarsWShadow, {
  useSyncScrolls,
  HScrollContainer,
  HScrollableContent,
} from 'app/common/ui-components/h-scrollbars-w-shadow'

import { LeftTable } from './left-table'
import { MainTable } from './main-table'
import { RightTable } from './right-table'
import { CellDataType, RowDataType, TableData } from './types'
import { defaultRenderCell } from './default-render-cell'
import { defaultRenderLoadingCell } from './default-render-loading-cell'
import { StatusMessage } from '../page-template'

import styles from './composite-table-template.module.scss'

const cx = cn.bind(styles)
const sameTableRowBaseClassName = 'row_'
const sameTableColumnBaseClassName = 'column_'

type Props<
  ColumnKey extends string,
  HeaderCellData,
  CellData extends CellDataType<ColumnKey>,
  RowData extends RowDataType<ColumnKey, CellData>,
  FooterCellData extends CellDataType<ColumnKey>,
  FooterRowData extends RowDataType<ColumnKey, FooterCellData>,
> = {
  data: TableData<ColumnKey, HeaderCellData, CellData, RowData, FooterCellData, FooterRowData>
  renderHeaderCell?: ({ columnKey }: { columnKey: string }) => ReactNode
  renderCell?: (cellData?: CellData) => ReactNode
  renderLoadingCell?: (columnKey: ColumnKey) => ReactNode
  renderFooterCell?: (cellData?: FooterCellData) => ReactNode
  loading?: boolean
  emptyDataMessage?: ReactNode
  loadingRowsCount?: number
  onContentSizeOverflow?: (isOverflow: boolean) => void
  disableTopSticky?: boolean
  columnHighlight?: boolean
  cellsVerticalAlignTop?: boolean
  cellsVerticalAlignMiddle?: boolean
  cellsVerticalAlignBottom?: boolean
  borderTop?: boolean
  headerCellsVerticalAlignTop?: boolean
  headerCellsVerticalAlignBottom?: boolean
}

const CompositeTableTemplateNoMemo = <
  ColumnKey extends string,
  HeaderCellData,
  CellData extends CellDataType<ColumnKey>,
  RowData extends RowDataType<ColumnKey, CellData>,
  FooterCellData extends CellDataType<ColumnKey>,
  FooterRowData extends RowDataType<ColumnKey, FooterCellData>,
>({
  data,
  renderHeaderCell = defaultRenderCell,
  renderCell = defaultRenderCell,
  renderLoadingCell = defaultRenderLoadingCell,
  renderFooterCell = defaultRenderCell,
  loading,
  emptyDataMessage = 'Нет данных',
  loadingRowsCount = 5,
  onContentSizeOverflow,
  disableTopSticky,
  columnHighlight,
  cellsVerticalAlignTop: cvaTop,
  cellsVerticalAlignMiddle: cvaMiddle,
  cellsVerticalAlignBottom: cvaBottom,
  borderTop,
  headerCellsVerticalAlignTop,
  headerCellsVerticalAlignBottom,
}: Props<ColumnKey, HeaderCellData, CellData, RowData, FooterCellData, FooterRowData>) => {
  // По умолчанию вертикальное выравнивание в ячейках по верху (кроме заголовков)
  const cellsVerticalAlignTop = cvaTop || (!cvaMiddle && !cvaBottom)
  const cellsVerticalAlignMiddle = cvaMiddle
  const cellsVerticalAlignBottom = cvaBottom

  const [isContentBiggerThanContainer, setIsContentBiggerThanContainer] = useState(false)
  const contentSizeOverflow = useCallback(
    (isOverflow: boolean) => {
      setIsContentBiggerThanContainer(isOverflow)
      onContentSizeOverflow?.(isOverflow)
    },
    [onContentSizeOverflow],
  )
  const [leftTableWidth, setLeftTableWidth] = useState(0)
  const [rightTableWidth, setRightTableWidth] = useState(0)

  const { total, data: tableData, columnKeysRight, columnKeysRightHeaders } = data
  const nothing = isEmpty(tableData)

  // копирование ширины ячеек таблицы middle в другие таблицы
  const [rowCellsCopyWidthsHeadersFrom, rowCellsCopyWidthsHeadersTo] = useCopyCellsOfTr(data)
  const [rowCellsCopyWidthsFootersFrom, rowCellsCopyWidthsFootersTo] = useCopyCellsOfTr(data)

  // синхронизация скролингов top, middle и bottom таблиц
  const [topScrollerProps, middleScrollerProps, bottomScrollerProps, updateBottomScroll] =
    useSyncScrolls()

  // копирование ширины всей middle таблицы в ширину bottom и top таблиц
  const [widthTableSource, widthTableTarget] = useCopyContentWidths(1, updateBottomScroll)

  // подсветка строки при наведении
  const [handleMouseEnter, handleMouseLeave] = useHoverByClass(sameTableRowBaseClassName)

  // подсветка столбца при наведении
  const [handleMouseCellEnter, handleMouseCellLeave] = useHoverByClass(sameTableColumnBaseClassName)

  // синхронизация высот ячеек левой и главной таблиц
  const updateEveryRender = []
  const [sameHeightRow] = useSyncRowsHeightsV2({
    numbersOfChunks: 1,
    // unobserveKey: data,
    updateKey: updateEveryRender,
  })

  const tableCommonProps = {
    data,
    renderHeaderCell,
    renderCell,
    renderLoadingCell,
    renderFooterCell,
    sameTableRowBaseClassName,
    sameTableColumnBaseClassName,
    loading,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    loadingRowsCount,
    ...(columnHighlight
      ? {
          onCellMouseEnter: handleMouseCellEnter,
          onCellMouseLeave: handleMouseCellLeave,
        }
      : {}),
    cellsVerticalAlignTop,
    cellsVerticalAlignMiddle,
    cellsVerticalAlignBottom,
    headerCellsVerticalAlignTop,
    headerCellsVerticalAlignBottom,
  }

  return (
    <>
      {/* Прилипаемая верхняя часть таблицы */}
      <Sticky disabled={disableTopSticky}>
        {(ref, sticky, shadow) => (
          <HScrollContainer ref={ref} className={cx({ shadow, borderTop })}>
            <LeftTable
              position="top"
              className={styles.partFixedLeft}
              rowReference={sameHeightRow}
              sticky={sticky}
              {...tableCommonProps}
            />

            <HScrollableContent>
              <HScrollbarsWShadow
                hideVerticalScrollBar
                hideHorizontalScrollBar
                absolutelyHideHorizontal_FixFF
                {...topScrollerProps}
              >
                <MainTable
                  position="top"
                  reference={widthTableTarget}
                  rowReferenceForCopyHeight={sameHeightRow}
                  rowReferenceForCopyCellWidthsForHeaders={rowCellsCopyWidthsHeadersTo}
                  className={styles.partScrollable}
                  sticky={sticky}
                  hasRightTable={!!(columnKeysRight && columnKeysRightHeaders)}
                  {...tableCommonProps}
                />
              </HScrollbarsWShadow>
            </HScrollableContent>

            {columnKeysRight && columnKeysRightHeaders && (
              <RightTable
                position="top"
                className={styles.partFixedRight}
                rowReference={sameHeightRow}
                sticky={sticky}
                columnKeysRight={columnKeysRight}
                columnKeysRightHeaders={columnKeysRightHeaders}
                {...tableCommonProps}
              />
            )}
          </HScrollContainer>
        )}
      </Sticky>

      {/* Середина таблицы с горизонтальным скролингом */}
      <div>
        {nothing && !loading && <StatusMessage>{emptyDataMessage}</StatusMessage>}

        {(!nothing || loading) && (
          <HScrollContainer>
            <LeftTable
              position="middle"
              className={styles.partFixedLeft}
              rowReference={sameHeightRow}
              onLeftTableSize={setLeftTableWidth}
              {...tableCommonProps}
            />

            <HScrollableContent>
              <HScrollbarsWShadow
                className={styles.shadowContainer}
                hideVerticalScrollBar
                hideHorizontalScrollBar
                absolutelyHideHorizontal_FixFF
                onChangeContentSize={contentSizeOverflow}
                {...middleScrollerProps}
              >
                <MainTable
                  position="middle"
                  reference={widthTableSource}
                  rowReferenceForCopyHeight={sameHeightRow}
                  rowReferenceForCopyCellWidthsForHeaders={rowCellsCopyWidthsHeadersFrom}
                  rowReferenceForCopyCellWidthsForFooters={rowCellsCopyWidthsFootersFrom}
                  className={styles.partScrollable}
                  hasRightTable={!!(columnKeysRight && columnKeysRightHeaders)}
                  {...tableCommonProps}
                />
              </HScrollbarsWShadow>
            </HScrollableContent>

            {columnKeysRight && columnKeysRightHeaders && (
              <RightTable
                position="middle"
                className={styles.partFixedRight}
                rowReference={sameHeightRow}
                onRightTableSize={setRightTableWidth}
                columnKeysRight={columnKeysRight}
                columnKeysRightHeaders={columnKeysRightHeaders}
                {...tableCommonProps}
              />
            )}
          </HScrollContainer>
        )}
      </div>

      {/* Прилипаемая нижняя часть таблицы */}
      <Sticky stickyBottom>
        {(ref, sticky, shadow) => (
          <HScrollContainer
            ref={ref}
            className={cx(styles.footer, {
              shadowTop: shadow,
              borderTop: !sticky,
            })}
          >
            {(total || isContentBiggerThanContainer) && (
              <>
                {total ? (
                  <LeftTable
                    position="bottom"
                    className={styles.partFixedLeft}
                    rowReference={sameHeightRow}
                    sticky={sticky}
                    {...tableCommonProps}
                  />
                ) : (
                  <div
                    className={cx(styles.emptyFooter, { sticky })}
                    style={{ width: leftTableWidth }}
                  />
                )}

                <HScrollableContent>
                  <HScrollbarsWShadow hideVerticalScrollBar disableShadow {...bottomScrollerProps}>
                    {total ? (
                      <MainTable
                        position="bottom"
                        reference={widthTableTarget}
                        rowReferenceForCopyHeight={sameHeightRow}
                        rowReferenceForCopyCellWidthsForFooters={rowCellsCopyWidthsFootersTo}
                        className={styles.partScrollable}
                        sticky={sticky}
                        hasRightTable={!!(columnKeysRight && columnKeysRightHeaders)}
                        {...tableCommonProps}
                      />
                    ) : (
                      <div className={cx(styles.emptyFooter, { sticky })} ref={widthTableTarget} />
                    )}
                  </HScrollbarsWShadow>
                </HScrollableContent>

                {columnKeysRight &&
                  columnKeysRightHeaders &&
                  (total ? (
                    <RightTable
                      position="bottom"
                      className={styles.partFixedRight}
                      rowReference={sameHeightRow}
                      sticky={sticky}
                      columnKeysRight={columnKeysRight}
                      columnKeysRightHeaders={columnKeysRightHeaders}
                      {...tableCommonProps}
                    />
                  ) : (
                    <div
                      className={cx(styles.emptyFooter, { sticky })}
                      style={{ width: rightTableWidth }}
                    />
                  ))}
              </>
            )}
          </HScrollContainer>
        )}
      </Sticky>
    </>
  )
}

export const CompositeTableTemplate = memo(
  CompositeTableTemplateNoMemo,
) as typeof CompositeTableTemplateNoMemo
